<script lang="ts" setup>
import { useSharedState } from '@/runtime/composables/useSharedState';
import { usePersistentRef } from '@/runtime/composables/usePersistentRef';
import { watch } from 'vue';
import { LayoutProps } from './LayoutProps';

const props = withDefaults(defineProps<LayoutProps>(), {
  collapse: false
});

const emit = defineEmits<{
  'update:collapse': [value: boolean];
}>();

// Use shared state as source of truth
const state = useSharedState<LayoutProps>(props);

// Persist the collapse state with a flag to prevent circular updates
let isPersisting = false;

if (state.collapse) {
  const persistedCollapse = usePersistentRef(state.collapse, {
    key: 'e-layout:collapsed',
    scope: 'global'
  });

  // Watch state changes and emit to parent (but not during persistence load)
  watch(
    () => state.collapse?.value,
    newValue => {
      if (isPersisting) {
        return;
      }

      emit('update:collapse', newValue ?? false);
    }
  );

  // Watch persisted value changes (from storage/hydration) and sync to state
  watch(
    () => persistedCollapse.value,
    newValue => {
      if (state.collapse && state.collapse.value !== newValue) {
        console.log('[Layout] Persisted value changed, syncing to state:', newValue);
        isPersisting = true;
        state.collapse.value = newValue;
        emit('update:collapse', newValue ?? false);

        // Reset flag after sync completes
        setTimeout(() => {
          isPersisting = false;
        }, 0);
      }
    }
  );
}
</script>

<template>
  <div
    class="e-layout"
    :class="[state.collapse?.value && 'e-layout-collapsed']"
    :data-open="!state.collapse?.value"
  >
    <div class="e-layout-navigation">
      <slot name="navigation" />
    </div>
    <div class="e-layout-page-content">
      <slot />
    </div>
  </div>
</template>
